import axios, { AxiosError } from 'axios';

const baseURL = '/api';

export const api = axios.create({ baseURL, withCredentials: false });

let accessToken: string | null = null;
let refreshToken: string | null = null;
let onUnauthorized: (() => void) | null = null;

/** Payload de um bloqueio por plano (402 vindo do FeatureGuard ou do
 *  enforcement de assentos/unidades). */
export interface PlanLimitInfo {
  error: string; // 'plan_required' | 'plan_limit_reached'
  message?: string;
  currentPlan?: string;
  requiredPlan?: string;
  limit?: number;
  limitKind?: string;
  featureLabel?: string;
}
let onPlanLimit: ((info: PlanLimitInfo) => void) | null = null;

/** Registra o handler global do modal de upgrade (chamado em 402 de plano). */
export function onPlanLimitReached(cb: (info: PlanLimitInfo) => void) {
  onPlanLimit = cb;
}

export function setTokens(a: string | null, r: string | null) {
  accessToken = a;
  refreshToken = r;
  if (a) localStorage.setItem('dentalflow.access', a);
  else localStorage.removeItem('dentalflow.access');
  if (r) localStorage.setItem('dentalflow.refresh', r);
  else localStorage.removeItem('dentalflow.refresh');
}

export function restoreTokens(): { access: string | null; refresh: string | null } {
  accessToken = localStorage.getItem('dentalflow.access');
  refreshToken = localStorage.getItem('dentalflow.refresh');
  return { access: accessToken, refresh: refreshToken };
}

/** Acessor read-only — usado por EventSource (SSE) que não suporta
 *  Authorization header e precisa do token via `?access_token=`. */
export function getAccessToken(): string | null {
  return accessToken;
}

export function onSessionExpired(cb: () => void) {
  onUnauthorized = cb;
}

api.interceptors.request.use((config) => {
  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
  return config;
});

let refreshPromise: Promise<string | null> | null = null;

async function doRefresh(): Promise<string | null> {
  if (!refreshToken) return null;
  try {
    const { data } = await axios.post(`${baseURL}/auth/refresh`, { refreshToken });
    setTokens(data.accessToken, data.refreshToken);
    return data.accessToken as string;
  } catch {
    setTokens(null, null);
    onUnauthorized?.();
    return null;
  }
}

api.interceptors.response.use(
  (r) => r,
  async (err: AxiosError) => {
    const original = err.config as (typeof err.config & { _retry?: boolean }) | undefined;
    if (err.response?.status === 401 && original && !original._retry) {
      original._retry = true;
      if (!refreshPromise) refreshPromise = doRefresh().finally(() => (refreshPromise = null));
      const newToken = await refreshPromise;
      if (newToken && original.headers) {
        original.headers.Authorization = `Bearer ${newToken}`;
        return api.request(original);
      }
    }
    // Bloqueio por plano (402): abre o modal de upgrade global. O past-due
    // (subscription_inactive) NÃO entra aqui — é tratado pelo PaywallOverlay.
    if (err.response?.status === 402) {
      const data = err.response.data as PlanLimitInfo | undefined;
      if (
        data &&
        (data.error === 'plan_required' || data.error === 'plan_limit_reached')
      ) {
        onPlanLimit?.(data);
      }
    }
    return Promise.reject(err);
  },
);
